import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import confetti from "canvas-confetti";
import "../login/login.css";
import "../landing/landing.css";
import "./FinalePassport.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getHuntResult, getLeaderboard } from "../../functions/checkpointGame";
import { s3PublicUrl } from "../../utils/s3PublicUrl";
import { BADGES, BadgeShape } from "../../components/BadgeShape/BadgeShape";

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function formatClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function ordinal(n) {
  if (!Number.isFinite(n)) return "—";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function ThankYou() {
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";

  useEffect(() => {
    // "login-static-bg" locks html/body overflow:hidden so the fixed background never scrolls —
    // this page's content (stats, passport badge grid, leaderboard button, footer) can run taller
    // than one viewport, so the extra "fp-body" class scopes a scroll-enabling override back on
    // for just this page (see FinalePassport.css).
    document.body.classList.add("login-static-bg", "fp-body");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg", "fp-body");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  const storedUserData = useMemo(readUserData, []);
  const gameSettings = useMemo(readGameSettings, []);
  const stageId = location.state?.stageId ?? gameSettings.stageId ?? null;
  const themeName = location.state?.themeName ?? gameSettings.themeName ?? "theme0";
  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? storedUserData.sessionId ?? "";
    const oid = user?.organizationId ?? storedUserData.organizationId ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user, storedUserData]);

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  // Persisted so a refresh while looking at the leaderboard stays on the leaderboard instead of
  // silently dropping back to the finale screen — sessionStorage (not localStorage) so it's
  // scoped to this browser tab/hunt session, same lifetime as the rest of this page's state.
  const [view, setView] = useState(() => sessionStorage.getItem("thankyou-view") === "leaderboard" ? "leaderboard" : "finale"); // 'finale' | 'leaderboard'
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  useEffect(() => {
    if (!tokenForApi || (!stageId && !uid)) return;
    (async () => {
      try {
        const resp = await getHuntResult({ token: tokenForApi, stageId, userId: uid, themeName });
        setResult(resp);
        confetti({ particleCount: 160, spread: 90, origin: { y: 0.5 } });
      } catch {
        // ignore — page still renders with defaults below
      } finally {
        setLoading(false);
      }
    })();
  }, [tokenForApi, stageId, uid, themeName]);

  const loadLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const resp = await getLeaderboard({ token: tokenForApi, themeName, limit: 5 });
      setLeaderboard(resp.leaderboard || []);
    } catch {
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const openLeaderboard = () => {
    sessionStorage.setItem("thankyou-view", "leaderboard");
    setView("leaderboard");
    if (!leaderboard) loadLeaderboard();
  };

  // Refresh landed straight on the leaderboard (restored above) — the data itself was never
  // fetched this session, so pull it in once the token/theme this needs is actually ready.
  useEffect(() => {
    if (view === "leaderboard" && !leaderboard && !leaderboardLoading && tokenForApi) {
      loadLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, tokenForApi]);

  if (loading) {
    return (
      <div className="landing-page">
        <div className="fp-loading">Tallying your results...</div>
      </div>
    );
  }

  const totalRounds = result?.totalRounds || 0;
  const roundsCompleted = result?.roundsCompleted || 0;

  if (view === "leaderboard") {
    return (
      <div className="landing-page">
        <div className="landing-content fp-leaderboard-content">
          <h1 className="fp-title" style={{ color: headingColor }}>
            Leaderboard
          </h1>
          {leaderboardLoading ? (
            <div className="fp-loading">Loading...</div>
          ) : (
            <ul className="fp-leaderboard-list">
              {(leaderboard || []).map((row) => {
                const isYourTeam = stageId != null && String(row.stageId) === String(stageId);
                return (
                  <li
                    key={row.rank}
                    className={`fp-leaderboard-row${isYourTeam ? " fp-leaderboard-row--you" : ""}`}
                  >
                    <span className="fp-leaderboard-rank" style={{ color: buttonBg }}>
                      {ordinal(row.rank)}
                    </span>
                    <span className="fp-leaderboard-team">
                      {row.teamName}
                      {row.teamNumber ? ` (#${row.teamNumber})` : ""}
                      {isYourTeam && <span className="fp-leaderboard-you-tag">You</span>}
                    </span>
                    <span className="fp-leaderboard-score">{row.score} pts</span>
                  </li>
                );
              })}
              {leaderboard && leaderboard.length === 0 && <li className="fp-loading">No teams finished yet.</li>}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="fp-blob fp-blob--blue" />
      <div className="fp-blob fp-blob--mint" />
      <div className="fp-blob fp-blob--pink" />

      <div className="landing-content fp-content">
        <div className="fp-eyebrow">HUNT COMPLETE</div>
        <h1 className="fp-title">{result?.teamName || "Your team"}</h1>

        <div className="fp-stats-row">
          <div className="fp-stat-tile">
            <div className="fp-stat-label">RANK</div>
            <div className="fp-stat-value">{ordinal(result?.rank)}</div>
          </div>
          <div className="fp-stat-tile">
            <div className="fp-stat-label">POINTS</div>
            <div className="fp-stat-value">{result?.totalScore ?? 0}</div>
          </div>
          <div className="fp-stat-tile">
            <div className="fp-stat-label">TIME</div>
            <div className="fp-stat-value">{formatClock(result?.elapsedSeconds)}</div>
          </div>
        </div>

        <div className="fp-passport-card">
          <div className="fp-passport-label">
            PASSPORT · {roundsCompleted} OF {totalRounds} EARNED
          </div>
          <div className="fp-badge-grid">
            {Array.from({ length: totalRounds }).map((_, i) => {
              const earned = i < roundsCompleted;
              const badge = BADGES[i % BADGES.length];
              return (
                <div key={i} className="fp-badge">
                  <BadgeShape shape={badge.shape} earned={earned} bg={badge.bg} fg={badge.fg} icon={badge.icon} />
                </div>
              );
            })}
          </div>
        </div>

        {result?.leaderboardEnabled !== false && (
          <button
            type="button"
            className="landing-play-btn fp-leaderboard-btn"
            style={{ background: buttonBg, color: buttonText }}
            onClick={openLeaderboard}
          >
            See the leaderboard
          </button>
        )}

        <div className="landing-footer">Powered by ExtraMile Play</div>
      </div>
    </div>
  );
}

export default ThankYou;
